import axios from 'axios';
import { showSnackbar } from '../../store/snackbarSlice';
import { showInsufficientPermissionModal } from '../../store/permissionModalSlice';
import store from '../../store';
import { logout } from '../auth/authSlice';
import appConfig from '../../config/appConfig';

const apiClient = axios.create({
  baseURL: appConfig.apiBaseUrl,
  withCredentials: true,
});

export function normalizeApiError(error) {
  const responseData = error?.response?.data;

  if (responseData?.error) {
    return { message: responseData.error, fieldErrors: {} };
  }

  if (responseData?.errors) {
    if (typeof responseData.errors === 'string') {
      return { message: responseData.errors, fieldErrors: {} };
    }

    const fieldErrors = responseData.errors;
    return {
      message: fieldErrors.message || 'Validation failed',
      fieldErrors,
    };
  }

  return { message: error?.message || 'Unexpected error', fieldErrors: {} };
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      store.dispatch(showInsufficientPermissionModal());
    }

    return Promise.reject(error);
  },
);

const authApiClient = axios.create({
  baseURL: `${appConfig.authBaseUrl}/auth`,
  withCredentials: true,
});

export async function endSession() {
  try {
    await authApiClient.post('/logout', {}, { skipSessionRedirect: true });
  } catch {
    // Clearing the cookie is best-effort when the network call fails.
  }

  store.dispatch(logout());
}

let sessionExpiryStarted = false;

function expireAuthenticatedSession() {
  if (!store.getState().auth?.isAuthenticated || sessionExpiryStarted) {
    return;
  }

  sessionExpiryStarted = true;
  store.dispatch(
    showSnackbar({
      message: 'Session expired. Please log in again.',
      severity: 'error',
    }),
  );
  endSession().finally(() => {
    window.location.href = '/login';
  });
}

const einvoicingApiClient = axios.create({
  baseURL: appConfig.authBaseUrl,
  withCredentials: true,
});

einvoicingApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      store.dispatch(showInsufficientPermissionModal());
    }

    return Promise.reject(error);
  },
);

authApiClient.interceptors.response.use(
  (response) => {
    if (response.status === 202) {
      store.dispatch(
        showSnackbar({
          message: response.data?.message || 'Request accepted successfully',
          severity: 'success',
        }),
      );
    }
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          if (error.config?.skipSessionRedirect) {
            break;
          }
          expireAuthenticatedSession();
          break;
        case 400:
          break;
        case 403:
          store.dispatch(showInsufficientPermissionModal());
          break;
        default:
          store.dispatch(
            showSnackbar({
              message: error.response.data.message || 'An unexpected error occurred',
              severity: 'error',
            }),
          );
      }
    }
    return Promise.reject(error);
  },
);

export { apiClient, authApiClient, einvoicingApiClient };
